import { PreviousComparison } from '@/common/api/params/mediaDriver'
import { dateStr } from '@/common/form/date'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import SummaryHeading from '@/components/view/atoms/heading/SummaryHeading'
import Icon, { IconType } from '@/components/view/atoms/icon/Icon'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Line from '@/components/view/layouts/area/Line'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'

interface Props {
  comparison: PreviousComparison
}

// 前回比較（トレンドキーワード変動・広告環境の変化。直近2回のサイト調査結果を比較）
const MediaPreviousComparisonPanel = ({ comparison }: Props) => {
  return (
    <>
      <SummaryHeading
        subText={
          comparison.available && comparison.previous.created_at && comparison.current.created_at
            ? `${dateStr(comparison.previous.created_at)} → ${dateStr(comparison.current.created_at)}`
            : undefined
        }
      >
        前回比較
      </SummaryHeading>
      <Area bottomMargin={16}>
        <Line />
        <AreaContent>
          {!comparison.available ? (
            <Text size={12} color={TextColor.SECONDARY} align='center'>
              {comparison.message}
            </Text>
          ) : (
            <>
              {(comparison.new_trends.length > 0 || comparison.dropped_trends.length > 0) && (
                <div className='flex flex-col gap-1.5'>
                  <Text size={12} weight='bold'>
                    トレンドキーワード変動
                  </Text>
                  <SummaryItemList shadow>
                    <SummaryItem color='secondary'>
                      <Text size={12} weight='bold'>
                        新規追加
                      </Text>
                      <ItemAlign gapX={4} align='baseline'>
                        <Text size={24} weight='bold' color={TextColor.SUCCESS}>
                          {comparison.new_trends.length}
                        </Text>
                        <Text size={12}>件</Text>
                      </ItemAlign>
                      <ItemAlign gapX={4} gapY={4}>
                        {comparison.new_trends.map((kw) => (
                          <Badge
                            key={`new-${kw}`}
                            size={10}
                            badgeType={BadgeType.KEYWORD_ON_BG_FILL}
                          >
                            {kw}
                          </Badge>
                        ))}
                      </ItemAlign>
                    </SummaryItem>
                    <SummaryItem color='secondary'>
                      <Text size={12} weight='bold'>
                        消失
                      </Text>
                      <ItemAlign gapX={4} align='baseline'>
                        <Text size={24} weight='bold' color={TextColor.SUCCESS}>
                          {comparison.dropped_trends.length}
                        </Text>
                        <Text size={12}>件</Text>
                      </ItemAlign>
                      <ItemAlign gapX={4} gapY={4}>
                        {comparison.dropped_trends.map((kw) => (
                          <Badge
                            key={`dropped-${kw}`}
                            size={10}
                            badgeType={BadgeType.KEYWORD_ON_BG_FILL}
                          >
                            {kw}
                          </Badge>
                        ))}
                      </ItemAlign>
                    </SummaryItem>
                  </SummaryItemList>
                </div>
              )}

              {comparison.ad_changes.length > 0 && (
                <div className='flex flex-col gap-1.5'>
                  <Text size={12} weight='bold'>
                    広告環境の変化
                  </Text>
                  <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
                    {comparison.ad_changes.map((ad) => (
                      <div
                        key={ad.keyword}
                        className='bg-secondary flex items-center justify-between gap-2 rounded-lg px-3 py-1.5'
                      >
                        <Badge size={12} badgeType={BadgeType.KEYWORD_ON_BG_FILL}>
                          {ad.keyword}
                        </Badge>
                        {ad.cpc_change !== 0 && (
                          <div className='flex items-center gap-1'>
                            <Badge size={12} badgeType={BadgeType.SQUARE_TERTIARY} fixed>
                              CPC {ad.cpc_change > 0 ? '+' : ''}
                            </Badge>
                            <Text
                              size={12}
                              color={ad.cpc_change > 0 ? TextColor.CRITICAL : TextColor.SUCCESS}
                            >
                              {ad.cpc_change}円（{ad.previous_cpc ?? '-'} → {ad.current_cpc ?? '-'}
                              ）
                            </Text>
                            <Icon
                              type={ad.cpc_change > 0 ? IconType.INCREASE : IconType.DECREASE}
                              color={ad.cpc_change > 0 ? 'critical' : 'success'}
                              size={12}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {comparison.new_trends.length === 0 &&
                comparison.dropped_trends.length === 0 &&
                comparison.ad_changes.length === 0 && (
                  <Text size={12} color={TextColor.SECONDARY} align='center'>
                    前回からの目立った変化はありません
                  </Text>
                )}
            </>
          )}
        </AreaContent>
      </Area>
    </>
  )
}

export default MediaPreviousComparisonPanel
